import {
  coreTeamFor,
  ROSTER_MISSING_EMAIL,
  type RosterEntry,
} from "@repo/vihangam-yog-shared/roster";
import { ChevronDown, MailCheck, Send } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import { CHAPTER } from "../../chapter";
import { friendlyError } from "../../lib/friendlyError";
import { trpc } from "../../trpc";

/** The people this chapter already knows about; empty outside Sydney. */
const CORE_TEAM = coreTeamFor(CHAPTER.id);

/** Team access's "Invite someone" button scrolls here and focuses the name. */
export const INVITE_SECTION_ID = "invite-someone";
export const INVITE_NAME_FIELD_ID = "invite-name";
const INVITE_EMAIL_FIELD_ID = "invite-email";

/** The confirm button appears where the Invite button was, so the second
 *  tap of a double-tap would land on it. Ignore taps this soon after. */
const CONFIRM_DEAD_MS = 400;

/** Plain words for why one invitation didn't go. */
function notSentSentence(error: string | undefined): string {
  if (error && /already has an account|pending invite/i.test(error)) {
    return "Not sent. They already have an account or an invitation waiting. Change their access in the list above instead.";
  }
  if (error && /valid email address/i.test(error)) {
    return "Not sent. That doesn't look like a valid email address. Check the spelling.";
  }
  return "Not sent. Something went wrong. Try again later.";
}

function peopleCount(n: number): string {
  return `${n} ${n === 1 ? "person" : "people"}`;
}

const fieldClass =
  "min-h-11 w-full rounded-md border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-3 text-body font-normal text-[color:var(--color-ink)] outline-none placeholder:text-[color:var(--color-ink-faint)] focus:border-[color:var(--color-amber)]";

/**
 * Invite people to the team app. Everyone invited joins as a team member
 * (the backend procedure keeps its old `inviteCoordinators` name, but it
 * grants the team member role and takes no program scope); an organiser
 * can raise their access in the list above once they have signed in.
 *
 * Nothing is preselected: the Sydney planning-sheet roster was invited in
 * one go already, so it waits behind a closed disclosure. Nobody is
 * invited until the send is confirmed here, because an invitation is a
 * real email landing in a real person's inbox.
 */
export function InviteTeam() {
  const invite = trpc.inviteCoordinators.useMutation();
  const [selected, setSelected] = useState<string[]>([]);
  const [armed, setArmed] = useState(false);
  const armedAt = useRef(0);
  const [extras, setExtras] = useState<RosterEntry[]>([]);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [addError, setAddError] = useState<string | null>(null);

  const people = useMemo(() => [...CORE_TEAM, ...extras], [extras]);

  function addPerson() {
    const email = newEmail.trim();
    const name = newName.trim();
    if (!email) {
      return;
    }
    // Require a 2+ char TLD so we don't accept an address the server's
    // stricter validator will reject (e.g. "name@host.c").
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setAddError("That doesn't look like an email address.");
      return;
    }
    if (people.some((p) => p.email.toLowerCase() === email.toLowerCase())) {
      setAddError("They're already on the list.");
      return;
    }
    setAddError(null);
    setExtras((prev) => [...prev, { name: name || email, email }]);
    setSelected((prev) => [...prev, email]);
    setNewName("");
    setNewEmail("");
    setArmed(false);
  }

  const results = invite.data?.results ?? [];
  const resultByEmail = new Map(results.map((r) => [r.email, r]));

  function toggle(email: string) {
    setArmed(false);
    setSelected((prev) =>
      prev.includes(email) ? prev.filter((e) => e !== email) : [...prev, email],
    );
  }

  const rosterEmails = CORE_TEAM.map((p) => p.email);
  const allRosterSelected =
    CORE_TEAM.length > 0 && rosterEmails.every((e) => selected.includes(e));

  function toggleWholeRoster() {
    setArmed(false);
    setSelected((prev) =>
      allRosterSelected
        ? prev.filter((e) => !rosterEmails.includes(e))
        : [...prev, ...rosterEmails.filter((e) => !prev.includes(e))],
    );
  }

  function arm() {
    armedAt.current = Date.now();
    setArmed(true);
  }

  function send() {
    if (Date.now() - armedAt.current < CONFIRM_DEAD_MS) {
      return;
    }
    invite.mutate({ emails: selected }, { onSettled: () => setArmed(false) });
  }

  const cantSendReason =
    selected.length === 0
      ? "Tick at least one person or add an email address."
      : null;

  return (
    <section
      id={INVITE_SECTION_ID}
      className="mt-12 scroll-mt-6 border-t border-[color:var(--color-line)] pt-8"
    >
      <h2 className="text-heading font-bold text-[color:var(--color-ink)]">
        Invite someone
      </h2>
      <p className="mt-2 max-w-2xl text-body text-[color:var(--color-ink-soft)]">
        Each person gets an email with a link to sign in. Once they have, they
        appear in the list above as a team member. Make them an organiser there
        if they&apos;ll run Reception or the guest list.
      </p>

      {/* ── Add by email ── */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
        <label
          htmlFor={INVITE_NAME_FIELD_ID}
          className="field-label w-full sm:w-56"
        >
          Name (optional)
          <input
            id={INVITE_NAME_FIELD_ID}
            type="text"
            autoComplete="off"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className={fieldClass}
          />
        </label>
        <label
          htmlFor={INVITE_EMAIL_FIELD_ID}
          className="field-label min-w-0 flex-1 sm:min-w-64"
        >
          Email address
          <input
            id={INVITE_EMAIL_FIELD_ID}
            type="email"
            autoComplete="off"
            value={newEmail}
            onChange={(e) => {
              setNewEmail(e.target.value);
              setAddError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addPerson();
              }
            }}
            className={fieldClass}
          />
        </label>
        <button
          type="button"
          onClick={addPerson}
          disabled={!newEmail.trim()}
          className="min-h-11 rounded-full border border-[color:var(--color-line-strong)] px-5 text-label font-semibold text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add to the list
        </button>
      </div>
      {addError ? (
        <p
          role="alert"
          className="mt-2 text-label text-[color:var(--color-danger)]"
        >
          {addError}
        </p>
      ) : null}

      {/* ── Who is getting an invitation ── */}
      {extras.length > 0 ? (
        <div className="mt-6">
          <p className="text-label text-[color:var(--color-ink-soft)]">
            Added: {extras.map((e) => e.name).join(", ")}. Once they sign in,
            they&apos;ll stay in the list above.
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {extras.map((person) => (
              <InviteeRow
                key={person.email}
                person={person}
                checked={selected.includes(person.email)}
                onToggle={() => toggle(person.email)}
                result={resultByEmail.get(person.email)}
              />
            ))}
          </ul>
        </div>
      ) : null}

      {CORE_TEAM.length > 0 ? (
        <details className="group mt-6 rounded-lg border border-[color:var(--color-line)]">
          <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 px-4 py-2 text-body font-semibold text-[color:var(--color-ink)] [&::-webkit-details-marker]:hidden">
            People from the Sydney planning sheet ({CORE_TEAM.length})
            <ChevronDown
              size={18}
              aria-hidden="true"
              className="shrink-0 text-[color:var(--color-ink-soft)] transition-transform group-open:rotate-180"
            />
          </summary>
          <div className="px-4 pb-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <p className="text-label text-[color:var(--color-ink-soft)]">
                Most of them have been invited already. Tick someone only if
                they need a new invitation.
              </p>
              <button
                type="button"
                onClick={toggleWholeRoster}
                className="min-h-11 text-label font-semibold text-[color:var(--color-amber)] underline underline-offset-2"
              >
                {allRosterSelected ? "Clear all" : "Select everyone"}
              </button>
            </div>
            <ul className="mt-2 grid gap-2 sm:grid-cols-2">
              {CORE_TEAM.map((person) => (
                <InviteeRow
                  key={person.email}
                  person={person}
                  checked={selected.includes(person.email)}
                  onToggle={() => toggle(person.email)}
                  result={resultByEmail.get(person.email)}
                />
              ))}
            </ul>
            {ROSTER_MISSING_EMAIL.length > 0 ? (
              <p className="mt-3 text-label text-[color:var(--color-ink-soft)]">
                We don&apos;t have an email address for{" "}
                {ROSTER_MISSING_EMAIL.join(", ")} yet.
              </p>
            ) : null}
          </div>
        </details>
      ) : null}

      {/* ── Send, with the consequence stated ── */}
      <div className="mt-6">
        {armed ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[color:var(--color-amber)]/50 bg-[color:var(--color-amber)]/8 p-4">
            <p className="w-full text-body text-[color:var(--color-ink)]">
              This sends an email to {peopleCount(selected.length)} now.
            </p>
            <button
              type="button"
              disabled={invite.isPending}
              onClick={send}
              className="inline-flex min-h-11 items-center gap-2 rounded-full bg-[color:var(--color-ink)] px-5 text-label font-semibold text-[color:var(--color-paper)] transition-opacity hover:opacity-85 disabled:opacity-50"
            >
              <Send size={16} aria-hidden="true" />
              {invite.isPending ? "Sending…" : "Yes, send the invitations"}
            </button>
            <button
              type="button"
              disabled={invite.isPending}
              onClick={() => setArmed(false)}
              className="min-h-11 rounded-full border border-[color:var(--color-line-strong)] px-5 text-label font-semibold text-[color:var(--color-ink)] hover:border-[color:var(--color-ink)] disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={cantSendReason !== null}
              onClick={arm}
              className="inline-flex min-h-11 items-center gap-2 rounded-full border border-[color:var(--color-amber)]/60 bg-[color:var(--color-amber)]/10 px-5 text-label font-semibold text-[color:var(--color-amber)] transition-colors hover:bg-[color:var(--color-amber)]/18 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MailCheck size={16} aria-hidden="true" />
              {selected.length === 0
                ? "Invite people"
                : `Invite ${peopleCount(selected.length)}`}
            </button>
            {cantSendReason ? (
              <p className="text-label text-[color:var(--color-ink-soft)]">
                {cantSendReason}
              </p>
            ) : null}
          </div>
        )}
      </div>

      {invite.data ? (
        <p
          role="status"
          className="mt-3 text-body text-[color:var(--color-ink)]"
        >
          {invite.data.sent}{" "}
          {invite.data.sent === 1 ? "invitation" : "invitations"} sent.
          {invite.data.failed > 0
            ? ` ${invite.data.failed} not sent. The reason is under each name.`
            : ""}
        </p>
      ) : null}
      {invite.error ? (
        <p
          role="alert"
          className="mt-3 text-label text-[color:var(--color-danger)]"
        >
          Couldn&apos;t send the invitations.{" "}
          {friendlyError(
            invite.error,
            "Check your internet connection and try again.",
          )}
        </p>
      ) : null}
    </section>
  );
}

function InviteeRow({
  person,
  checked,
  onToggle,
  result,
}: {
  person: RosterEntry;
  checked: boolean;
  onToggle: () => void;
  result: { ok: boolean; error?: string } | undefined;
}) {
  return (
    <li>
      <label className="surface flex min-h-11 cursor-pointer items-start gap-3 px-3.5 py-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-0.5 h-5 w-5 shrink-0 accent-[color:var(--color-amber)]"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-body font-semibold text-[color:var(--color-ink)]">
            {person.name}
            {person.alias ? (
              <span className="ml-1.5 text-label font-normal text-[color:var(--color-ink-soft)]">
                (also called {person.alias})
              </span>
            ) : null}
          </span>
          <span className="block text-label break-words text-[color:var(--color-ink-soft)]">
            {person.email}
          </span>
          {result ? (
            <span
              className={`mt-1 block text-label font-semibold ${
                result.ok
                  ? "text-[color:var(--color-success)]"
                  : "text-[color:var(--color-danger)]"
              }`}
            >
              {result.ok ? "Invitation sent" : notSentSentence(result.error)}
            </span>
          ) : null}
        </span>
      </label>
    </li>
  );
}
